import type { CommitChange, CommitGroupChange, HookContext } from '@colplat/schema';
import { defineApp } from '@colplat/schema';

/**
 * GnuCash-model double-entry accounting, migrated from legacy PHP + vanilla
 * JS. Full rebuild inventory: docs/legacy/webledger.inventory.md — rebuild is
 * not complete until every section there reaches 100%.
 *
 * Scope: the core ledger — currencies, the account tree, purposes,
 * transactions/splits, templates, attachments — plus the bank-sync subsystem
 * (bankConnection/bankAccount/bankRule/bankSyncLog/transactionBankRef), which
 * was scoped as a follow-up milestone and has since landed. Export/import
 * (confirmed required, inventory §5) is a services/api concern layered on top
 * of these entities, not a manifest one.
 *
 * Document groups: `transaction`+`split` and `template`+`templateLine` commit
 * as one atomic unit via `HistoryWriter.commitDocumentGroup` (ADR-0021), not
 * as independent per-record document-mode commits — a transaction's
 * `SUM(splits.value) = 0` invariant spans every split, exactly the case
 * ADR-0021 exists for.
 *
 * `currency.code` is this app's natural business key, not its record id —
 * every entity still gets the platform's standard UUIDv7 `id` (hard
 * invariant #8, and `packages/migrate`'s DDL generator has no other option),
 * but other entities reference a currency by `code` (a plain indexed
 * string), never by that id. See inventory §0.2.
 *
 * `account.parentId` and `templateFolder.parentId` are self-referential
 * trees. Deliberately NOT declared via `entity.parent` — that relation
 * assumes a distinct parent entity, and a self-referential one would recurse
 * forever in `packages/migrate`'s dependency-ordering walk. Tree queries use
 * `ctx.queryByField('account', 'parentId', …)` in hooks instead of
 * `queryChildren`.
 *
 * Two known platform gaps this manifest works around rather than silently
 * ignores, both worth fixing at the framework level before they bite a
 * second app:
 * - `currency.code` and `purpose.name` uniqueness is enforced here via a
 *   `beforeCommit` existence check rather than a database constraint. Fine
 *   for a handful of admin-curated rows, and now a choice rather than a
 *   limitation: the DDL generator gained `indexes: [{ columns, unique }]`
 *   for bank sync's de-dup key, which legacy is explicit must be
 *   DB-enforced. These two could follow whenever it is worth a migration.
 * - `FieldDef.required`/`max`/`min`/`enumValues` are not enforced by
 *   `core-server`'s write path today — they are UI-facing metadata only.
 *   The hooks below re-check the handful of constraints that actually
 *   protect data integrity (currency existence, the balance invariant,
 *   cycle guards); they do not re-implement generic bounds-checking.
 */

export const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const;

async function fieldValueOf(ctx: HookContext, entity: string, recordId: string, field: string): Promise<string | undefined> {
  const value = await ctx.read(entity, recordId, field);
  return value && value.t !== 'null' ? String(value.v) : undefined;
}

/**
 * Every cross-field rule from legacy's `account_save`, ported to fire per
 * field at commit time (field-mode commits one field per blur, not a whole
 * form at once — see the manifest's own doc comment on why this fits a
 * collaborative editor better than one big form-submit validation).
 */
async function validateAccountChange(ctx: HookContext, change: CommitChange): Promise<void> {
  if (change.field === 'currencyCode') {
    if (change.value.t !== 'string') throw new Error('Unknown currency');
    const known = await ctx.queryByField('currency', 'code', change.value);
    if (known.length === 0) throw new Error('Unknown currency');

    const isMulti = await fieldValueOf(ctx, 'account', change.recordId, 'isMulticurrency');
    if (isMulti !== 'true') {
      const used = await ctx.queryByJsonMember('transaction', 'legs', 'accountId', change.recordId);
      if (used.length > 0) throw new Error('Account has transactions; currency cannot be changed');
    }
  }

  if (change.field === 'isPlaceholder' && change.value.t === 'bool' && change.value.v) {
    const used = await ctx.queryByJsonMember('transaction', 'legs', 'accountId', change.recordId);
    if (used.length > 0) throw new Error('Account has transactions; it cannot become a placeholder');
  }

  if (change.field === 'parentId' && change.value.t === 'uuid') {
    if (change.value.v === change.recordId) throw new Error('Account cannot be its own parent');
    // Walk the new parent's own ancestor chain looking for a cycle back to
    // this account — WITH a visited set. Legacy's identical walk in
    // account_save has none, which is a documented bug (inventory §7): a
    // pre-existing cycle elsewhere in the tree hangs the request. `break`
    // on a revisit here instead treats that as "not this write's problem".
    let cursor: string | null = change.value.v;
    const seen = new Set<string>();
    while (cursor) {
      if (cursor === change.recordId) throw new Error('Cannot move an account under its own descendant');
      if (seen.has(cursor)) break;
      seen.add(cursor);
      const parent = await ctx.read('account', cursor, 'parentId');
      cursor = parent && parent.t === 'uuid' ? parent.v : null;
    }
  }

  if (change.field === 'type' && change.value.t === 'string') {
    if (!ACCOUNT_TYPES.includes(change.value.v as (typeof ACCOUNT_TYPES)[number])) {
      throw new Error('Invalid account type');
    }
  }
}

/**
 * A wallet's name is unique among its SIBLINGS, not globally — "Renovation /
 * 66" and "Maintenance / 66" are both wanted, and both are called 66. So this
 * cannot use `validateUniqueField`, which is global by construction.
 *
 * Both directions matter: renaming into a clash and re-parenting into one are
 * the same mistake arriving from different fields.
 */
async function validateWalletName(ctx: HookContext, change: CommitChange): Promise<void> {
  if (change.field !== 'name' && change.field !== 'parentId') return;

  const name = change.field === 'name'
    ? (change.value.t === 'string' ? change.value.v : undefined)
    : await fieldValueOf(ctx, 'wallet', change.recordId, 'name');
  const parentId = change.field === 'parentId'
    ? (change.value.t === 'uuid' ? change.value.v : null)
    : (await fieldValueOf(ctx, 'wallet', change.recordId, 'parentId')) ?? null;
  if (name === undefined) return;

  const sameName = await ctx.queryByField('wallet', 'name', { t: 'string', v: name });
  for (const match of sameName) {
    if (match.id === change.recordId) continue;
    const theirParent = (await fieldValueOf(ctx, 'wallet', match.id, 'parentId')) ?? null;
    if (theirParent === parentId) {
      throw new Error(parentId
        ? `There is already a wallet called "${name}" inside that one`
        : `There is already a top-level wallet called "${name}"`);
    }
  }
}

/**
 * Same cycle guard as `account.parentId`, and the same visited set for the
 * same reason: a pre-existing cycle elsewhere in the tree is not this write's
 * problem, and hanging the request over it helps nobody.
 */
async function validateWalletParent(ctx: HookContext, change: CommitChange): Promise<void> {
  if (change.field !== 'parentId' || change.value.t !== 'uuid') return;
  if (change.value.v === change.recordId) throw new Error('A wallet cannot be inside itself');
  let cursor: string | null = change.value.v;
  const seen = new Set<string>();
  while (cursor) {
    if (cursor === change.recordId) throw new Error('A wallet cannot be moved inside one of its own');
    if (seen.has(cursor)) break;
    seen.add(cursor);
    const parent: string | undefined = await fieldValueOf(ctx, 'wallet', cursor, 'parentId');
    cursor = parent ?? null;
  }
}

/**
 * A budget's own rules (ADR-0024). Deliberately short: a budget is advisory,
 * so almost nothing about it is worth refusing. These two are, because both
 * produce a figure that cannot be read rather than one somebody disagrees
 * with — a limit in a currency that does not exist has no units, and a limit
 * of zero or less is not a budget, it is a ban, and the ledger has no way to
 * express what one would mean.
 *
 * Notably absent: any check that spending stays inside the limit. Going over
 * never blocks a write, here or anywhere else — see ADR-0023 for the argument,
 * which is about offline entry being unable to answer the question honestly,
 * not about politeness.
 */
async function validateBudgetChange(ctx: HookContext, change: CommitChange): Promise<void> {
  if (change.field === 'currencyCode') {
    if (change.value.t !== 'string') throw new Error('Unknown currency');
    const known = await ctx.queryByField('currency', 'code', change.value);
    if (known.length === 0) throw new Error('Unknown currency');
  }
  if (change.field === 'amount') {
    if (change.value.t !== 'decimal') throw new Error('A budget needs a limit');
    if (Number(change.value.v) <= 0) throw new Error('A budget has to be more than nothing');
  }
}

/** Interim uniqueness check on one specific field — see the manifest's doc comment on the missing DDL primitive. */
async function validateUniqueField(
  ctx: HookContext, entity: string, field: string, change: CommitChange, message: string,
): Promise<void> {
  if (change.field !== field || change.value.t !== 'string') return;
  const matches = await ctx.queryByField(entity, field, change.value);
  if (matches.some((m) => m.id !== change.recordId)) throw new Error(message);
}

/**
 * The one rule that matters most in this whole app (SPEC.md's own words):
 * `SUM(splits.value) = 0`, tolerance 0.005, plus "at least two splits".
 *
 * Reads the transaction's own `legs` POST-write — the commit has already
 * written it inside the still-open transaction by the time this hook runs
 * (ADR-0021), so this sees the real proposed final state rather than a
 * value it would have to reconstruct from the request. That was true when
 * the legs were sibling rows read through `queryChildren` and it is true
 * now that they are an array on the row being written; only where to look
 * changed (ADR-0026).
 */
async function assertSplitsBalance(ctx: HookContext, transactionId: string): Promise<void> {
  const stored = await ctx.read('transaction', transactionId, 'legs');
  const legs = stored !== undefined && stored.t === 'json' && Array.isArray(stored.v)
    ? stored.v as Array<Record<string, unknown>>
    : [];
  if (legs.length < 2) throw new Error('A transaction needs at least two splits');

  let sum = 0;
  for (const leg of legs) {
    /*
     * Money is a string in the array, as it is in the column and on the
     * wire, and `Number` is applied here only to add. A leg that lost its
     * value, or arrived holding a JSON number because something built the
     * array without the CAST, is a leg this must refuse rather than quietly
     * treat as zero — an unbalanced transaction is the one thing this app
     * must never store.
     */
    const value = leg['value'];
    if (typeof value !== 'string' || value === '' || !Number.isFinite(Number(value))) {
      throw new Error('Every split needs a value');
    }
    sum += Number(value);
  }
  if (Math.abs(sum) > 0.005) {
    throw new Error(`Transaction does not balance (off by ${sum.toFixed(4)})`);
  }
}

/**
 * Every debt between wallets fits inside the leg it was taken from.
 *
 * A debt is recorded against one leg, for all of it or part (ADR-0023,
 * amendment of 2026-10-18). "Part" is a stored figure, so something has to
 * keep it honest: never more than the leg, never zero or less. Checked here,
 * post-write like the balance, so it sees the legs and the debts as this
 * commit leaves them — an edit that shrinks a leg under a debt taken from it
 * is refused just as surely as a debt typed larger than its leg.
 *
 * A row with no `amount` is the whole leg and cannot exceed it. A named leg
 * that is gone is refused only when the row ALSO carries an amount: a
 * whole-leg row pointing nowhere is what the read path already tolerates
 * (it counts for nothing), while a part of nothing is a figure nobody can
 * check.
 */
async function assertTransfersFitLegs(ctx: HookContext, transactionId: string): Promise<void> {
  const transfers = await ctx.queryChildren('walletTransfer', 'transactionId', transactionId);
  if (transfers.length === 0) return;
  const stored = await ctx.read('transaction', transactionId, 'legs');
  const legs = stored !== undefined && stored.t === 'json' && Array.isArray(stored.v)
    ? stored.v as Array<Record<string, unknown>>
    : [];
  /*
   * Per leg, everything claimed from it: one payment can be partly for the
   * car and partly for the renovation, but not for more than was paid. A row
   * with no amount claims the whole leg.
   */
  const claimed = new Map<string, number>();
  for (const transfer of transfers) {
    const raw = transfer.fields['amount'];
    const part = raw === undefined || raw.t === 'null' || raw.v === null || raw.v === ''
      ? undefined : Number(raw.v);
    if (part !== undefined && (!Number.isFinite(part) || part <= 0)) {
      throw new Error('A debt between wallets needs an amount above zero');
    }
    const legId = transfer.fields['amountSplitId']?.v;
    const leg = typeof legId === 'string' ? legs.find((l) => l['id'] === legId) : undefined;
    if (!leg) {
      if (part !== undefined) throw new Error('A debt between wallets names a leg this entry no longer has');
      continue;
    }
    const size = Math.abs(Number(leg['amount']));
    const total = (claimed.get(legId as string) ?? 0) + (part ?? size);
    claimed.set(legId as string, total);
    if (total > size + 0.00005) {
      throw new Error(`Debts between wallets cannot add up to more than their leg (${total.toFixed(4)} > ${size.toFixed(4)})`);
    }
  }
}

async function assertGroupChangeValid(ctx: HookContext, change: CommitGroupChange): Promise<void> {
  if (change.parent.entity === 'transaction') {
    await assertSplitsBalance(ctx, change.parent.recordId);
    await assertTransfersFitLegs(ctx, change.parent.recordId);
  }
  // template/templateLine has no cross-row invariant to check — a template
  // is saved atomically because that is how the editor presents it (one
  // form, one save), not because an unbalanced template is unsafe the way
  // an unbalanced transaction is.
}

export default defineApp({
  id: 'webledger',
  // 2 (2026-09-21): added `tag` and `transactionTag`. Purely additive — an
  // older client cannot write fields it has never heard of — but the version
  // is what tells a long-lived offline tab to reload rather than keep a
  // half-known schema, which is exactly what it is for.
  // 3 (2026-09-24): `wallet`, `walletTransfer` and `split.walletId` (ADR-0023).
  // 4 (2026-09-29): added `budget` (ADR-0024). Additive again — one new table,
  // nothing existing moved, so there is no backfill.
  // 5 (2026-10-07): `budget.hidden`. Additive, defaults false, so every
  // existing budget stays exactly as visible as it was.
  // 6 (2026-10-11): `wallet.shortName`. Additive and nullable — a wallet
  // without one is shown by its full name, so nothing has to be set for the
  // ledger to read as it did.
  // 7 (2026-10-12): `walletTransfer.amountSplitId`, and more than one transfer
  // per transaction. Additive and nullable; a row without one falls back to
  // the old guess, which is what `backfill-transfer-legs` fills in.
  // 8 (2026-10-12): `wallet.color`. Additive and nullable — a wallet without
  // one is drawn in a palette colour, so nothing has to be set and there is
  // no backfill.
  // 9 (2026-10-14): `currency.sortOrder`, set by dragging on the Settings
  // page. Additive and nullable — unordered currencies keep the alphabetical
  // order they always had, so there is no backfill.
  // 10 (2026-10-18): `walletTransfer.amount` — a debt for part of a leg, on
  // any transaction (ADR-0023, amendment of 2026-10-18). Additive and
  // nullable: a row without one is the whole leg, exactly as before, so there
  // is no backfill.
  // 11 (2026-10-22): `transaction.currentlyOwnedBy`/`ownedSince` dropped
  // from the manifest — holds replaced them (ADR-0003). Bumped so that a page
  // still running the old client, whose holds lapse and renew, reloads into
  // the one that knows holds no longer do.
  schemaVersion: 11,
  roles: ['member', 'webledger-admin'],

  entities: {
    currency: {
      editMode: 'field',
      offline: 'full',
      projection: 'currencies',
      fields: {
        created: { type: 'datetime', system: true },
        code: { type: 'string', max: 3, required: true, indexed: true, label: 'Code' },
        name: { type: 'string', max: 50, required: true },
        symbol: { type: 'string', max: 8 },
        fraction: { type: 'int', min: 0, max: 4, label: 'Decimal places' },
        useForTotals: { type: 'bool', label: 'Offer for folder totals' },
        /**
         * Where this currency comes in any list of currencies — the columns
         * of a breakdown, the lines of a chart. Set by dragging on the
         * Settings page; a book kept mostly in forints wants them first, not
         * after AUD and CAD because of the alphabet. Nullable: unset sorts as
         * 0 and then by code, which is exactly the order before this field.
         */
        sortOrder: { type: 'int' },
      },
    },

    account: {
      editMode: 'field',
      offline: 'full',
      projection: 'accounts',
      fields: {
        created: { type: 'datetime', system: true },
        parentId: { type: 'uuid', indexed: true, label: 'Parent account' },
        name: { type: 'string', max: 100, required: true, indexed: true },
        type: { type: 'string', max: 10, enumValues: ACCOUNT_TYPES, required: true },
        currencyCode: { type: 'string', max: 3, required: true, label: 'Currency' },
        isPlaceholder: { type: 'bool', label: 'Placeholder (folder — cannot hold transactions)' },
        isMulticurrency: { type: 'bool', label: 'Multi-currency' },
        sortOrder: { type: 'int' },
        description: { type: 'string', max: 255 },
        /**
         * The icon's filename, as legacy stores it — `000-assets.svg`, not a
         * path or a URL. The seeded set ships as app assets
         * (apps/webledger/assets/README.md) and is served from `/icons/`, so
         * a bare filename is all the database needs and nothing here knows
         * its mount path (invariant #10).
         *
         * Still a filename rather than the "asset reference" the inventory
         * anticipates, because that reference only becomes meaningful once
         * there is an upload mechanism to produce one — and designing it
         * ahead of that would be guessing at its shape. A missing file
         * renders the fallback, which is what legacy does too.
         */
        iconPath: { type: 'string', max: 255, lease: 'none' },
      },
      indexes: [['parentId', 'sortOrder']],
    },

    purpose: {
      editMode: 'field',
      offline: 'full',
      projection: 'purposes',
      fields: {
        created: { type: 'datetime', system: true },
        name: { type: 'string', max: 80, required: true, indexed: true },
        sortOrder: { type: 'int' },
        /** Same as `account.iconPath`. */
        iconPath: { type: 'string', max: 255, lease: 'none' },
      },
    },

    /**
     * An uploaded account or purpose icon, held in the database rather than
     * on disk.
     *
     * Bytes in a row because an icon is a few kilobytes from a small,
     * admin-curated set: the row is backed up with everything that references
     * it, cannot fall out of step with it, and needs no second restore path.
     *
     * The original comment here justified this with "colplat has no writable
     * upload directory", which is simply false — `ops/scripts/deploy.sh`
     * creates `/opt/colplat/<env>/var` and the systemd units grant it under
     * `ProtectSystem=strict`. The conclusion survives for icons on the size
     * argument alone; the false premise does not, and it should never have
     * been repeated to a second subsystem. It was, to attachments, whose
     * bytes now live on disk where legacy keeps them — see
     * services/api/src/attachment-store.ts.
     *
     * `data` is base64 in a `TEXT` column, which caps a stored icon at
     * roughly 48 KB of original bytes. That is ample for the vector icons
     * this set is made of, and the client downscales anything raster to
     * 128px before it ever gets here — a 64px icon has no business being
     * larger, and downscaling in the browser avoids an image library in
     * `api` (which, per CLAUDE.md, would need a reason).
     */
    icon: {
      editMode: 'field',
      offline: 'full',
      projection: 'icons',
      fields: {
        created: { type: 'datetime', system: true },
        mime: { type: 'string', max: 100, required: true, lease: 'none' },
        data: { type: 'text', required: true, lease: 'none' },
      },
    },

    /** Pure many-to-many join, no attributes of its own — see the manifest's doc comment on why this isn't an array field. */
    accountPurpose: {
      editMode: 'field',
      offline: 'full',
      projection: 'account_purposes',
      parent: { entity: 'account', field: 'accountId' },
      fields: {
        created: { type: 'datetime', system: true },
        accountId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        purposeId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
      },
      indexes: [['accountId', 'purposeId']],
    },

    /**
     * A free-form label on a **transaction** — "renovation", "holiday",
     * "reimbursable".
     *
     * Deliberately a second mechanism rather than reusing `purpose`, because
     * the two answer different questions and hang off different things. A
     * purpose tags an *account* ("every account earmarked for maintenance"),
     * which is what lets a purpose-filtered report say which legs count. A tag
     * names one *event* in the ledger — this dinner, this invoice — and every
     * leg of a tagged transaction is equally tagged. Folding them together
     * would mean one filter with two incompatible meanings.
     *
     * That difference is not cosmetic; see `transactionTag` for what it does
     * to a running total.
     */
    tag: {
      editMode: 'field',
      offline: 'full',
      projection: 'tags',
      fields: {
        created: { type: 'datetime', system: true },
        name: { type: 'string', max: 60, required: true, indexed: true },
        sortOrder: { type: 'int' },
      },
    },

    /**
     * Pure many-to-many join between a transaction and its tags — same shape
     * as `accountPurpose`, and not an array field for the same reason (see
     * this manifest's own doc comment).
     *
     * **The running-total consequence.** Every existing report filter narrows
     * by a property of an *account*, which is what lets it mark some legs
     * `inScope` and others not; the Running column is then the net of the
     * counted legs, and legacy's SPEC is explicit that the uncounted ones are
     * greyed rather than hidden ("hiding half a transaction would be a lie").
     * A tag has no such split — it belongs to the whole transaction — so
     * counting every leg of a tagged transaction sums a balanced document and
     * yields **zero, by construction**, for every row.
     *
     * So a tag filter scopes by account *type* instead: expense and income
     * legs count, the asset/liability funding side is greyed. "Tagged
     * renovation" then totals what the renovation cost, which is the question
     * a tag is asked. A tagged transfer between two asset accounts
     * contributes nothing, which is correct — a transfer is not a cost — and
     * still appears in the list in full.
     */
    transactionTag: {
      editMode: 'field',
      offline: 'full',
      projection: 'transaction_tags',
      parent: { entity: 'transaction', field: 'transactionId' },
      fields: {
        created: { type: 'datetime', system: true },
        transactionId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        tagId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
      },
      // Unique: a transaction carries a tag once. Two rows would double it in
      // every count and show the chip twice.
      indexes: [{ columns: ['transactionId', 'tagId'], unique: true }],
    },

    /**
     * An envelope. Not an account (that is where money *is*) and not a tag
     * (a tag is optional and overlapping). A wallet is a PARTITION of the
     * money: every money leg carries at most one, and a leg carrying none is
     * simply not yet earmarked. See ADR-0023.
     *
     * `parentId` is a self-referential tree, declared as a plain indexed uuid
     * rather than via `entity.parent` — for the identical reason `account`
     * and `templateFolder` do: that relation assumes a *distinct* parent
     * entity and a self-referential one recurses forever in
     * `packages/migrate`'s dependency-ordering walk.
     */
    wallet: {
      editMode: 'field',
      offline: 'full',
      projection: 'wallets',
      fields: {
        created: { type: 'datetime', system: true },
        name: { type: 'string', max: 60, required: true, indexed: true, label: 'Name' },
        /**
         * What to call this wallet where there is no room for its name.
         *
         * The register notches a leg's wallet into the figure's own border,
         * which is a few characters wide before it starts eliding — and a
         * row that collapses two wallets writes both of them and an arrow
         * between. "Renovation → Maintenance" is 24 characters for a fact
         * that "Renov. → Maint." states in 15.
         *
         * Optional, and falling back to `name`: a wallet nobody has
         * abbreviated reads exactly as it did. Not unique and not validated
         * against anything — it is a label for a notch, not an identifier,
         * and the full name is what every list, picker and accessible name
         * still says.
         */
        shortName: { type: 'string', max: 12, label: 'Short' },
        /**
         * The colour this wallet is drawn in where a chart splits money by
         * wallet — the accounts page, which shows whose an account's balance
         * is. `#rrggbb` from a colour picker.
         *
         * Optional: a wallet without one takes the next colour from a fixed
         * palette (`walletColours`), so the charts read the day this field
         * appears and nothing is written on anybody's behalf. Not validated
         * here beyond its length — the readers check the shape before they
         * paint with it, and treat anything else as unset.
         */
        color: { type: 'string', max: 7, label: 'Colour' },
        parentId: { type: 'uuid', indexed: true, label: 'Inside' },
        sortOrder: { type: 'int' },
      },
    },

    /**
     * The label on a transfer between two wallets, and the only thing that
     * distinguishes a loan from a gift.
     *
     * A wallet transfer is an ordinary balanced transaction whose two money
     * legs sit on the SAME account with different `walletId`s: cash does not
     * move, the earmark does. That shape needs no new machinery — but
     * "is this coming back?" is not derivable from it, which is what this
     * row records.
     *
     * Deliberately a sidecar rather than columns on `transaction`: the vast
     * majority of transactions are not transfers, and three permanently-null
     * columns on the busiest table in the app is the wrong trade. Same shape
     * as `transactionBankRef` for the same reason.
     *
     * Amount and currency are NOT stored here. They live on the splits, and
     * a second copy would be free to drift from them; the balance invariant
     * already governs the splits and nothing governs a denormalised echo.
     * Read the amount from the split whose `walletId` is `toWalletId`.
     */
    walletTransfer: {
      editMode: 'document',
      offline: 'none',
      projection: 'wallet_transfers',
      parent: { entity: 'transaction', field: 'transactionId' },
      fields: {
        created: { type: 'datetime', system: true },
        transactionId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        /** 'loan' (coming back) | 'grant' (given for good) | 'repay' (paying one back). */
        kind: { type: 'string', max: 8, required: true, lease: 'none' },
        fromWalletId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        toWalletId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        /**
         * Which leg this transfer moved — and therefore how much it was.
         *
         * The amount is still not stored here, which is ADR-0023's rule and
         * its reason: a second copy would be free to drift from the legs,
         * which the balance invariant already governs. What was missing is
         * WHICH leg to read it from. The transfer named two wallets, so the
         * read path had to guess, and its guess was "every positive leg in
         * the destination wallet" — exact at two legs, and at four it matched
         * more than once and counted one loan as several.
         *
         * Nullable, because rows written before this have none. They fall
         * back to the old rule, narrowed to one answer rather than many;
         * `migrate backfill-transfer-legs` fills in the ones it can resolve
         * and reports the ones a human has to look at.
         */
        amountSplitId: { type: 'uuid', indexed: true, lease: 'none' },
        /**
         * How much of that leg is owed — when it is not all of it.
         *
         * Not the copy ADR-0023 refused. That rule is about storing again a
         * figure the legs already hold; "Maintenance paid 32,407.42 and
         * 10,000 of it was for the car" is a figure the legs do NOT hold, and
         * there is nowhere else for it to live. Null means the whole leg,
         * which is every row written before this existed.
         *
         * Positive, in the leg's own currency, and never more than the leg:
         * `assertTransfersFitLegs` refuses the commit otherwise — including an
         * edit that shrinks the leg under it — rather than anything quietly
         * capping it.
         */
        amount: { type: 'decimal', scale: 4, lease: 'none' },
      },
      indexes: [['fromWalletId', 'toWalletId']],
    },

    /**
     * A spending limit for one tagged project, out of one wallet (ADR-0024).
     *
     * A budget is money you PLAN; a wallet is money you HOLD. That difference
     * is the whole reason this is not a sub-wallet: ring-fencing the cash
     * works today with no code at all — nest a wallet, transfer into it, and
     * an overrun shows up as an ordinary shortfall — but it forces every
     * incoming payment to be allocated to a room on arrival, and turns
     * "we moved 500k of the plan from room 1 to room 2" into a
     * `walletTransfer`, which this model reads as a loan or a gift. Both
     * routes are supported and they do not interact.
     *
     * Not fields on `tag`, for the reason `walletTransfer` is not columns on
     * `transaction`: most tags are never budgeted, and four permanently-null
     * columns on the vocabulary table is the wrong trade. A separate record
     * also lets phase 1 and phase 2 of the same room coexist, and lets one
     * tag carry a different limit in two different wallets.
     *
     * **Tags overlap and this cannot be made true by construction.** A
     * transaction tagged both `room#1` and `room#2` counts in full against
     * both budgets, and nothing here can stop it — the restriction that makes
     * `split.walletId` a partition has no equivalent for a tag. That was
     * disqualifying for wallets (ADR-0023) because the cash-on-hand identity
     * had to hold silently for ever; it is tolerable for a budget, which is
     * advisory and is read by someone who can see the transactions behind it.
     * So the overlap is DISPLAYED, never assumed away: the budget row names
     * how much it shares with another budget. The user-facing consequence is
     * that one receipt covering two rooms must be entered as two
     * transactions, because a tag belongs to the whole transaction.
     */
    budget: {
      editMode: 'field',
      offline: 'full',
      projection: 'budgets',
      fields: {
        created: { type: 'datetime', system: true },
        /** The project. A tag rather than an entity of its own — see ADR-0024. */
        tagId: { type: 'uuid', required: true, indexed: true, label: 'Project' },
        /**
         * Whose money, and therefore what counts: only spending whose money
         * leg sits in this wallet or any wallet INSIDE it. Descendants are
         * included for the same reason the wallet cards roll them up.
         *
         * NULL means any wallet, which is a real answer rather than a gap —
         * a project need not be funded out of one envelope.
         */
        walletId: { type: 'uuid', indexed: true, label: 'Wallet' },
        /**
         * The one currency this limit is written in. Spending in another
         * currency is reported beside it, never netted into it and never
         * converted here — same discipline as every other number on the
         * wallets page (ADR-0023). No rate is stored on a budget.
         */
        currencyCode: { type: 'string', max: 3, required: true, label: 'Currency' },
        amount: { type: 'decimal', scale: 4, required: true, label: 'Limit' },
        /** Both nullable: a project runs until it is done. No recurrence — see ADR-0024. */
        fromDate: { type: 'date', label: 'From' },
        toDate: { type: 'date', label: 'Until' },
        note: { type: 'string', max: 255, label: 'Note' },
        /**
         * Put away, not deleted.
         *
         * A project that is over stops being something to look at every
         * time the page opens, and goes on being something to look up: what
         * was budgeted, what was spent, who it was for. Deleting it would
         * answer the first need by destroying the second.
         *
         * So it is a flag on the budget rather than a date test — "finished"
         * is a judgement, not an arithmetic fact about `toDate`, and a
         * project can be done early or run past its own deadline. Hiding
         * changes nothing about what the budget counts; it is only whether
         * the page offers it unasked.
         */
        hidden: { type: 'bool', label: 'Hidden' },
      },
      indexes: [['tagId', 'walletId']],
    },

    transaction: {
      editMode: 'document',
      offline: 'create',
      projection: 'transactions',
      fields: {
        created: { type: 'datetime', system: true },
        txDate: { type: 'date', required: true, lease: 'none', label: 'Date' },
        description: { type: 'string', max: 255, lease: 'none' },
        reference: { type: 'string', max: 50, lease: 'none' },
        currencyCode: { type: 'string', max: 3, required: true, lease: 'none', label: 'Currency' },
        // Global monotonic register-ordering counter — a business field, not
        // a stand-in for HLC ordering. `(txDate, seq)` decides register
        // position; HLC governs write-conflict resolution, an unrelated
        // concern. See inventory §1.
        seq: { type: 'int', lease: 'none', indexed: true },
        /*
         * The transaction's legs, on the transaction (ADR-0026).
         *
         * Derived during the move: the `splits` table is still the source of
         * truth and this is recomputed from it inside the same transaction as
         * every write that touches a leg. Nothing reads it yet. When the
         * readers move over it stops being derived and becomes the record,
         * and `split` stops being an entity.
         *
         * `system: true` because, while it is derived, nobody may send it:
         * it is the server's to maintain, takes no lease, and its changes
         * belong to the commit that caused them rather than to a field of
         * its own.
         */
        legs: {
          /*
           * Not `system`. A system field is one a commit may not carry, and
           * the legs are now exactly what a save to a transaction carries —
           * writing them IS writing the document (ADR-0026 stage 4).
           *
           * That does not make them client input. The wire format is still
           * `splits: [...]` with an op on each, and the server folds that
           * into this array itself: every route that builds a transaction's
           * fields names `legs` in `buildFields`' skip list, so a body that
           * sends a `legs` array of its own is ignored rather than trusted.
           * The field definitions on `split` below are the validation each
           * leg still goes through.
           */
          type: 'json',
          /*
           * No `indexPaths`, and this is a measured decision rather than an
           * omission.
           *
           * A multi-valued index on `$[*].accountId` can be *created* — the
           * schema supports it and MySQL accepts it — but on MySQL 26.7 no
           * query form will use it. `MEMBER OF` with `->>`, with `->`, with
           * the literal cast or collated to match, `JSON_CONTAINS`, and even
           * `FORCE INDEX`, all plan a table scan; EXPLAIN shows the filter
           * expression matching the index expression exactly and the
           * optimiser declining it all the same. An index nothing can use is
           * write cost and disk for nothing, so it is not created.
           *
           * The in-use checks therefore scan, and at this app's size that is
           * the right answer rather than a concession. ADR-0025 already ships
           * the whole ledger to every device and scans it there in
           * JavaScript; a server scanning the same rows for something as rare
           * as deleting an account is not the thing that will hurt. ADR-0026
           * measures the ceiling: twenty thousand transactions after twenty
           * years.
           *
           * `FieldDef.indexPaths` stays in the schema. It is the right way to
           * ask for this and a later MySQL may well honour it; what is
           * recorded here is that today it does not.
           */
        },
        // No `currentlyOwnedBy`/`ownedSince` any more: who is editing a
        // transaction, and since when, is its hold (ADR-0003, 2026-10-22,
        // core-server/holds.ts). The two columns stay in the table — migrate
        // never drops one — and nothing writes them; `migrate` carried the
        // markers standing at the time over into holds.
      },
      indexes: [['txDate', 'seq']],
    },

    split: {
      /*
       * `split` has no table (ADR-0026 stage 4). A transaction's legs are an
       * array on the transaction, and this entity is what the app still
       * calls them: the name a query, a row type and a rights rule use.
       *
       * It is not replicated and cannot be committed to. A device reading
       * `Ledger.splits()` gets the legs off the transaction it already
       * holds, and a save writes them as a field of that transaction.
       *
       * That a transaction and its legs were separate records is what this
       * removed. A delta could page them apart, so a device could hold —
       * and draw — a transaction with legs missing or stale. One row cannot
       * be half-delivered.
       *
       * The field definitions below stay, and are still the validation a
       * save goes through: `legsFromInput` builds each leg through them
       * before it writes the array. They describe the shape of a leg; they
       * no longer describe columns.
       */
      derived: true,
      replicate: false,
      /*
       * `splits_v` is the legs, expanded (ADR-0026).
       *
       * Every server query that reads legs relationally goes through this
       * view, so pointing it at `transactions.legs` moves all of them at once
       * and leaves the device and the server reading the same bytes. The
       * table is still the write target and is still what `refreshLegs`
       * computes the column from; it is simply no longer what anything reads.
       *
       * Two of the columns are the transaction's rather than the leg's, and
       * that is now the truth rather than a convenience. A leg has no
       * version of its own — it is versioned with the document it is part of
       * — so `version` is the transaction's, which is exactly what an
       * editor must commit a leg change against. And `deleted_at` is a
       * constant NULL, because a leg that is gone is simply not in the
       * array; every reader filters on it and they should all keep
       * matching.
       *
       * Money comes back out through DECIMAL, never a float: the column
       * stores it as a string for exactly that reason, and a view that cast
       * it to DOUBLE would undo the care taken to put it there.
       */
      view: [
        'SELECT',
        '  j.`id` COLLATE utf8mb4_0900_ai_ci AS `id`,',
        '  BIN_TO_UUID(t.`id`, 0) AS `transactionId`,',
        '  j.`accountId` COLLATE utf8mb4_0900_ai_ci AS `accountId`,',
        '  j.`walletId` COLLATE utf8mb4_0900_ai_ci AS `walletId`,',
        '  j.`memo` AS `memo`,',
        '  CAST(j.`value` AS DECIMAL(20,4)) AS `value`,',
        '  CAST(j.`amount` AS DECIMAL(20,4)) AS `amount`,',
        '  j.`currencyCode` COLLATE utf8mb4_0900_ai_ci AS `currencyCode`,',
        '  CAST(NULL AS DATETIME) AS `deleted_at`,',
        '  t.`version` AS `version`,',
        '  t.`created` AS `created`,',
        '  t.`changed_at` AS `changed_at`',
        'FROM `transactions` t,',
        'JSON_TABLE(t.`legs`, \'$[*]\' COLUMNS (',
        '  `id` CHAR(36) PATH \'$.id\',',
        '  `accountId` CHAR(36) PATH \'$.accountId\',',
        '  `walletId` CHAR(36) PATH \'$.walletId\',',
        '  `memo` TEXT PATH \'$.memo\',',
        '  `value` VARCHAR(40) PATH \'$.value\',',
        '  `amount` VARCHAR(40) PATH \'$.amount\',',
        '  `currencyCode` CHAR(3) PATH \'$.currencyCode\'',
        ')) j',
        /*
         * The `COLLATE` on every id is not decoration. `JSON_TABLE` hands a
         * `CHAR` column back as `utf8mb4_general_ci` while `BIN_TO_UUID`
         * produces `utf8mb4_0900_ai_ci`, and MySQL refuses to compare the
         * two — so without it every `JOIN accounts_v a ON a.id =
         * s.accountId` in reports and the register dies at runtime with
         * "illegal mix of collations". Nothing in the test suite can see
         * this, because those tests stub the database.
         */
        /*
         * Deleting a transaction empties its `legs`, so in a consistent
         * database this filter selects nothing extra. It is here because
         * "in a consistent database" is the part worth not relying on.
         *
         * The old view was the table, where soft-deleting a transaction
         * soft-deleted its legs too, so every reader's `deleted_at IS NULL`
         * excluded them. This is that same guarantee restated where the
         * state now lives. Without it, a row that ever did hold legs past
         * its own deletion would put them back into registers and balances
         * — and the totals would still look right, because a transaction's
         * legs sum to zero. That is the kind of wrong that does not get
         * noticed, which is what makes one cheap `WHERE` worth keeping.
         *
         * It also means a restore cannot rebuild legs from the column: the
         * column is empty by then, and `recordRestore` puts them back from
         * the still-soft-deleted rows in the table. Something that has to
         * change before the table can go.
         */
        'WHERE t.`deleted_at` IS NULL',
      ].join('\n'),
      editMode: 'document',
      offline: 'create',
      projection: 'splits',
      parent: { entity: 'transaction', field: 'transactionId' },
      fields: {
        created: { type: 'datetime', system: true },
        transactionId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        accountId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        memo: { type: 'string', max: 255, lease: 'none' },
        // Accounting invariant: never rename, never collapse into one column.
        value: { type: 'decimal', scale: 4, required: true, lease: 'none', label: 'Value (transaction currency)' },
        amount: { type: 'decimal', scale: 4, required: true, lease: 'none', label: 'Amount (account currency)' },
        currencyCode: { type: 'string', max: 3, required: true, lease: 'none' },
        /**
         * The envelope the money on this leg is earmarked for (ADR-0023).
         *
         * Meaningful only on legs touching an ASSET or LIABILITY account, and
         * NULL everywhere else. That asymmetry is the design, not an
         * omission: it is what makes `SUM(wallet balances) = cash on hand`
         * true by construction. Put a wallet on the expense leg too and the
         * equation breaks silently, which is the failure mode a tag-based
         * design could never rule out.
         *
         * NULL means "not earmarked" and is a permanent, respectable state —
         * the client shows it as the Unassigned wallet. It is deliberately
         * not a foreign key to a real "Unassigned" row, so the backfill has
         * nothing to create and an un-earmarked leg costs nothing.
         */
        walletId: { type: 'uuid', indexed: true, lease: 'none', label: 'Wallet' },
      },
      indexes: [['transactionId', 'accountId']],
    },

    // ---------------------------------------------------------- bank sync
    //
    // PSD2 account information, read-only. The whole subsystem exists to turn
    // a bank's booked-entry feed into ordinary ledger transactions, once,
    // without ever booking the same entry twice. Everything below serves that
    // second clause.

    bankConnection: {
      editMode: 'document',
      offline: 'none',          // a consent flow needs the bank, and the bank needs the network
      // Replicated since 2026-09-30, minus the consent internals — see the
      // `replicate: false` fields below. The bank's NAME is what a reader
      // needs; the session that authorised the feed is not.
      projection: 'bank_connections',
      fields: {
        created: { type: 'datetime', system: true },
        provider: { type: 'string', max: 32, required: true, lease: 'none' },
        aspspName: { type: 'string', max: 150, required: true, lease: 'none', label: 'Bank' },
        aspspCountry: { type: 'string', max: 2, required: true, lease: 'none', label: 'Country' },
        psuType: { type: 'string', max: 16, enumValues: ['personal', 'business'], lease: 'none' },
        /** Enable Banking's session id for this consent. Not a secret, but not useful to anyone either. */
        sessionId: { type: 'string', max: 191, lease: 'none', replicate: false },
        /**
         * The `state` we generated when starting the consent, and match on
         * the way back. Indexed because the callback arrives with nothing
         * else to identify itself by.
         */
        reference: { type: 'string', max: 64, indexed: true, lease: 'none', replicate: false },
        status: {
          type: 'string', max: 16, required: true, lease: 'none',
          enumValues: ['pending', 'active', 'error', 'expired'],
        },
        errorMsg: { type: 'string', max: 255, lease: 'none' },
        authorizedAt: { type: 'datetime', lease: 'none' },
        /** When the consent lapses. What the bank granted, not what we asked for. */
        expiresAt: { type: 'datetime', lease: 'none' },
        /**
         * Who authorised this consent — the account holder, on whose behalf
         * every entry from this feed is booked.
         *
         * Needed because the scheduled sync runs with no session at all, and
         * `commitDocumentGroup` needs both an author for the history row and
         * that author's roles for the rights check. Legacy books cron imports
         * with `created_by = NULL`, which loses the one fact worth keeping:
         * these are *this person's* bank entries, and they are the person who
         * can revoke the consent.
         */
        /*
         * Not replicated (ADR-0025), for the same reason as the ownership
         * marker on `transaction` and with one extra of its own.
         *
         * Nothing shows it — it is an audit field the server sets once, right
         * after the connection row is created. It stays unreplicated on its
         * own merit rather than for want of a mechanism: since 2026-10-05 a
         * delta reads `changed_at` and would carry it perfectly well. A field
         * no screen reads is one no device should hold a copy of.
         */
        createdBy: { type: 'uuid', system: true, replicate: false },
      },
    },

    bankAccount: {
      editMode: 'document',
      offline: 'none',
      // Replicated since 2026-09-30, minus the provider internals — see the
      // `replicate: false` fields below.
      projection: 'bank_accounts',
      parent: { entity: 'bankConnection', field: 'connectionId' },
      fields: {
        created: { type: 'datetime', system: true },
        connectionId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        ebAccountUid: { type: 'string', max: 191, required: true, lease: 'none', replicate: false },
        iban: { type: 'string', max: 64, lease: 'none' },
        name: { type: 'string', max: 150, lease: 'none' },
        currency: { type: 'string', max: 3, lease: 'none' },
        /** The provider's own account payload, verbatim, for when a field turns out to matter later. */
        raw: { type: 'text', lease: 'none', replicate: false },
        /** The ledger account that *is* this bank account: every entry books its bank leg here. */
        ledgerAccountId: { type: 'uuid', indexed: true, lease: 'none', label: 'Ledger account' },
        /** The counter-leg used when no rule matches — "Uncategorised". */
        suspenseAccountId: { type: 'uuid', lease: 'none', label: 'Uncategorised account' },
        /** A "not before" cutoff. Narrows what the ledger accepts *and* what the bank must send. */
        importFrom: { type: 'date', lease: 'none', label: 'Not before' },
        lastSynced: { type: 'datetime', lease: 'none' },
        /** The newest entry actually booked — where the next fetch window starts. */
        lastTxnDate: { type: 'date', lease: 'none' },
      },
      // One feed account per connection. Re-running discovery after a
      // reconnect must update the row rather than add a second one.
      indexes: [{ columns: ['connectionId', 'ebAccountUid'], unique: true }],
    },

    bankSyncLog: {
      editMode: 'document',
      offline: 'none',
      // Online only (ADR-0025): a consent flow needs the bank, and a device
      // holding a stale copy of somebody's bank feed is a liability rather
      // than a feature.
      replicate: false,
      projection: 'bank_sync_log',
      parent: { entity: 'bankAccount', field: 'bankAccountId' },
      fields: {
        created: { type: 'datetime', system: true },
        bankAccountId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        syncedAt: { type: 'datetime', required: true, lease: 'none' },
        added: { type: 'int', lease: 'none' },
        skipped: { type: 'int', lease: 'none' },
        status: { type: 'string', max: 16, required: true, lease: 'none' },
        message: { type: 'string', max: 255, lease: 'none' },
      },
      // The daily cap counts rows since midnight for every account on one
      // *connection* (services/api's `connectionFetchesToday`), so the count
      // joins through `bankAccount`. This index is what makes each account's
      // half of that join cheap; the connection side is served by
      // `bankAccount.connectionId`'s own index.
      indexes: [['bankAccountId', 'syncedAt']],
    },

    /**
     * The feed entries behind a transaction — one, or several once two
     * entries have been merged into one economic event.
     *
     * `externalSrc` is derived from the *account's own identity* (its IBAN
     * and currency, or the provider's uid when it has no IBAN), never from a
     * `bankAccount` row id. Bank account rows do not survive a
     * disconnect/reconnect; the IBAN does. Keyed on a row id, every
     * previously imported entry would look unseen after a reconnect and the
     * whole history would import a second time.
     *
     * The currency is part of the key because one IBAN can carry several
     * balances — Wise and Revolut multi-currency accounts share an IBAN
     * across per-currency sub-accounts that arrive as separate feed accounts.
     *
     * Do not "simplify" this. Dropping the currency, or scoping the lookup to
     * the current bank account, re-breaks reconnect safety in a way that
     * stays invisible until somebody disconnects a bank and silently doubles
     * their ledger.
     */
    transactionBankRef: {
      editMode: 'document',
      offline: 'none',
      // Replicated since 2026-09-30: "which bank entry is this transaction"
      // is part of the transaction, and a device that cannot answer it
      // offline cannot show the row it already holds.
      projection: 'transaction_bank_refs',
      parent: { entity: 'transaction', field: 'transactionId' },
      fields: {
        created: { type: 'datetime', system: true },
        transactionId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        externalSrc: { type: 'string', max: 191, required: true, lease: 'none' },
        externalRef: { type: 'string', max: 191, required: true, lease: 'none' },
        bankAccountId: { type: 'uuid', indexed: true, lease: 'none' },
        bankRaw: { type: 'text', lease: 'none' },
      },
      // The de-duplication guarantee, and the reason `indexes` learned the
      // word `unique`. The lookup before an insert is the fast path; *this*
      // is the enforcement. Two syncs racing, or one whose application logic
      // is bypassed, must not be able to book an entry twice.
      indexes: [{ columns: ['externalSrc', 'externalRef'], unique: true }],
    },

    /**
     * An auto-categorisation rule.
     *
     * Written through named routes like the rest of this subsystem, not
     * through the generic field-commit path — hence `lease: 'none'`
     * throughout. This started as an `editMode: 'field'` entity on the
     * argument that a rule is a small thing several admins might tune at
     * once, and building the page is what disproved it: two of its fields
     * are enums and one is an account *reference*, none of which
     * `mountCollabField` can render (it would offer a raw UUID in a text
     * box). "Edited through the platform" was a promise the platform cannot
     * currently keep for this shape of record, so the rule editor is the
     * bank page's own form, saving whole.
     *
     * The cost, stated plainly: no field history and no leases on a rule.
     * Legacy has neither, a rule is configuration rather than ledger data,
     * and the records still get their created/deleted events. If generic
     * controls for enums and record references ever land, this is a good
     * candidate to move back.
     */
    bankRule: {
      editMode: 'document',
      offline: 'none',
      // Online only (ADR-0025): a consent flow needs the bank, and a device
      // holding a stale copy of somebody's bank feed is a liability rather
      // than a feature.
      replicate: false,
      projection: 'bank_rules',
      fields: {
        created: { type: 'datetime', system: true },
        matchField: {
          type: 'string', max: 16, required: true, lease: 'none',
          enumValues: ['any', 'description', 'counterparty'], label: 'Match on',
        },
        matchType: {
          type: 'string', max: 16, required: true, lease: 'none',
          enumValues: ['contains', 'equals', 'regex'], label: 'Match type',
        },
        pattern: { type: 'string', max: 255, required: true, lease: 'none', label: 'Pattern' },
        targetAccountId: { type: 'uuid', required: true, lease: 'none', label: 'Book the other leg to' },
        /** Ascending; the first match wins, so this is the whole of the conflict story. */
        priority: { type: 'int', lease: 'none', label: 'Priority' },
        enabled: { type: 'bool', lease: 'none' },
      },
      indexes: [['priority']],
    },

    templateFolder: {
      editMode: 'field',
      offline: 'full',
      projection: 'template_folders',
      fields: {
        created: { type: 'datetime', system: true },
        parentId: { type: 'uuid', indexed: true },
        name: { type: 'string', max: 100, required: true },
        sortOrder: { type: 'int' },
      },
    },

    template: {
      editMode: 'document',
      offline: 'create',
      projection: 'templates',
      fields: {
        created: { type: 'datetime', system: true },
        folderId: { type: 'uuid', lease: 'none' },
        name: { type: 'string', max: 150, required: true, lease: 'none' },
        sortOrder: { type: 'int', lease: 'none' },
        currencyCode: { type: 'string', max: 3, required: true, lease: 'none' },
        description: { type: 'string', max: 255, lease: 'none' },
        reference: { type: 'string', max: 50, lease: 'none' },
      },
    },

    /** No FK check against account on purpose — a template may outlive an account it references (matches legacy exactly). */
    templateLine: {
      editMode: 'document',
      offline: 'create',
      projection: 'template_lines',
      parent: { entity: 'template', field: 'templateId' },
      fields: {
        created: { type: 'datetime', system: true },
        templateId: { type: 'uuid', required: true, indexed: true, lease: 'none' },
        accountId: { type: 'uuid', lease: 'none' },
        memo: { type: 'string', max: 255, lease: 'none' },
        value: { type: 'decimal', scale: 4, lease: 'none' },
        amount: { type: 'decimal', scale: 4, lease: 'none' },
      },
    },

    /**
     * A file attached to a transaction — its metadata. The bytes are on disk.
     *
     * `storageKey` is that file's path relative to `<varDir>/attachments`,
     * `YYYY/MM/<uuid>` — the "opaque handle from wherever the upload
     * mechanism lands" this field was declared for, and opaque is meant: the
     * shape is `attachment-store.ts`'s business and nothing else parses it.
     *
     * The bytes spent one iteration in a row here, in an `attachmentBlob`
     * entity that no longer exists, on a premise that was false (see `icon`).
     * Keeping the metadata in the database and the bytes beside it on disk is
     * what legacy does and what an 8 MB object wants: a list of forty
     * transactions reads a few hundred bytes per file instead of several
     * megabytes, and a download streams instead of being assembled in
     * memory.
     *
     * `thumb` is the exception that proves it: a small inline data URL, made
     * in the browser at upload time, so a list renders without one request
     * per file. Only images get one; everything else shows its type.
     *
     * Field-mode: an attachment's own metadata has no cross-row invariant.
     */
    attachment: {
      editMode: 'field',
      offline: 'full',
      projection: 'attachments',
      fields: {
        created: { type: 'datetime', system: true },
        transactionId: { type: 'uuid', indexed: true },
        originalName: { type: 'string', max: 255, required: true },
        mime: { type: 'string', max: 100, required: true },
        storageKey: { type: 'string', max: 255, required: true, lease: 'none' },
        /** Bytes of the original, so a list can say "2.4 MB" without reading it. */
        size: { type: 'int', lease: 'none' },
        /** A small `data:` URL for images. Empty for everything else. */
        thumb: { type: 'text', lease: 'none' },
      },
    },
  },

  rights: {
    // Matches legacy: every member of this app may read and write every
    // record and field — no per-record or per-field rights, by deliberate
    // design (inventory §6), not an oversight to fix here. But "member" is
    // still required, matching `administration`'s own convention (see its
    // manifest): zero role grants for this app means 'hidden', never a
    // default. `roles` includes both 'member' and 'webledger-admin' —
    // either one is enough for baseline read/write.
    //
    // The six legacy admin-only actions (merge accounts, bulk-delete
    // transactions, import) are not per-field writes at all — they are
    // whole *operations*, gated the way legacy gates them: at the route
    // level in services/api, checking `ctx.roles.includes('webledger-admin')`
    // directly, not through this per-field resolver.
    resolve: (ctx) => (ctx.roles.length === 0 ? 'hidden' : 'write'),
  },

  hooks: {
    beforeCommit: async (ctx, change) => {
      if (change.entity === 'account') await validateAccountChange(ctx, change);
      if (change.entity === 'currency') await validateUniqueField(ctx, 'currency', 'code', change, 'A currency with that code already exists');
      if (change.entity === 'purpose') await validateUniqueField(ctx, 'purpose', 'name', change, 'A purpose with that name already exists');
      if (change.entity === 'tag') await validateUniqueField(ctx, 'tag', 'name', change, 'A tag with that name already exists');
      if (change.entity === 'wallet') {
        await validateWalletParent(ctx, change);
        await validateWalletName(ctx, change);
      }
      if (change.entity === 'budget') await validateBudgetChange(ctx, change);
    },
    beforeGroupCommit: assertGroupChangeValid,
  },

  views: {
    default: 'generated',
  },
});
